import { ITipo_Indice_Documento } from '../../services/api/tipo_indice/Tipo_Indice_Service';
import { IdComponente } from './IdComponente';

type LinhaIndice = Pick<ITipo_Indice_Documento, 'idIndice' | 'idComponente'>;

interface IIndiceValorOrigem {
    valor: string | null;
    dataInicial: string | null;
    dataFinal: string | null;
}

export interface IIndiceValorPayload {
    idIndice: number;
    valor: string | null;
    dataInicial: string | null;
    dataFinal: string | null;
}

/**
 * Converte o valor vindo do backend ({valor, dataInicial, dataFinal}) para a string
 * única usada pelos campos de índice na UI.
 */
const paraValorUI = (indiceValor?: IIndiceValorOrigem | null): string => {
    if (!indiceValor) {
        return '';
    }

    if (indiceValor.valor !== null && indiceValor.valor !== undefined) {
        return indiceValor.valor;
    }

    if (indiceValor.dataInicial !== null && indiceValor.dataFinal !== null) {
        return `${indiceValor.dataInicial} | ${indiceValor.dataFinal}`;
    }

    if (indiceValor.dataInicial !== null && indiceValor.dataFinal === null) {
        return indiceValor.dataInicial;
    }

    return '';
};

/**
 * Converte o array paralelo de valores-string da UI para o payload neutro que o
 * backend espera ({idIndice, valor, dataInicial, dataFinal}), sem idDocumento/idCaixa.
 */
const paraPayload = (rows: LinhaIndice[], valores: string[]): IIndiceValorPayload[] => {
    return rows.map((indice, index) => {
        let valor: string | null = valores[index] ?? '';
        let dataInicial: string | null = null;
        let dataFinal: string | null = null;

        if (indice.idComponente === IdComponente.DATA) {
            dataInicial = valor.trim() === '' ? null : valor;
            valor = null;
        }

        if (indice.idComponente === IdComponente.PERIODO) {
            const [inicio, fim] = (valor ?? '').split(' | ');
            dataInicial = inicio?.trim() ? inicio : null;
            dataFinal = fim?.trim() ? fim : null;
            valor = null;
        }

        if (indice.idComponente === IdComponente.CHECKBOX && valor !== null && valor !== undefined && valor !== '') {
            valor = valor
                .split(', ')
                .filter((item) => item.trim() !== '')
                .join(', ');
        }

        if (valor !== null && valor.trim() === '') {
            valor = null;
        }

        return {
            idIndice: indice.idIndice,
            valor,
            dataInicial,
            dataFinal,
        };
    });
};

/**
 * Monta o payload de pesquisa/filtro por índices: ignora índices vazios ou com o
 * valor sentinela de "nenhum", e explode cada opção marcada de checkbox em um
 * registro separado (comportamento esperado pelo endpoint de consulta por índices).
 */
const explodirParaFiltro = (
    rows: LinhaIndice[],
    valores: string[],
    opts?: { valorOpcaoNenhum?: string },
): IIndiceValorPayload[] => {
    const valorOpcaoNenhum = opts?.valorOpcaoNenhum ?? 'nenhum';
    const resultado: IIndiceValorPayload[] = [];

    rows.forEach((indice, index) => {
        const valorBruto = valores[index];

        if (valorBruto === undefined || valorBruto === null || valorBruto === '' || valorBruto === valorOpcaoNenhum) {
            return;
        }

        if (indice.idComponente === IdComponente.CHECKBOX) {
            valorBruto
                .split(', ')
                .filter((opcao) => opcao.trim() !== '')
                .forEach((opcao) => {
                    resultado.push({ idIndice: indice.idIndice, valor: opcao, dataInicial: null, dataFinal: null });
                });
            return;
        }

        let valor: string | null = valorBruto;
        let dataInicial: string | null = null;
        let dataFinal: string | null = null;

        if (indice.idComponente === IdComponente.DATA) {
            valor = null;
            dataInicial = valorBruto;
        }

        if (indice.idComponente === IdComponente.PERIODO) {
            valor = null;
            [dataInicial, dataFinal] = valorBruto.split(' | ');
        }

        resultado.push({ idIndice: indice.idIndice, valor, dataInicial, dataFinal });
    });

    return resultado;
};

export const ConversorDeValorIndice = {
    paraValorUI,
    paraPayload,
    explodirParaFiltro,
};
